import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useMediaQuery from '@/hooks/use-media-query'
import { createModal, useModalize } from '@/components/modalize/mui'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { Button } from '@/components/button'
import { LeftArrowIcon } from '@/components/icons'
import { useGetSuiteInfo } from '@/hooks/use-sponsored-listing'
import Map from './map.svg?react'
import ListingCard from '../components/listing-card'
import Separator from '../components/breadcrumb-separator.svg?react'
import { Row, Col } from '../styles'

// Styled Components
const Wrapper = styled.div`
  display: flex;
  background: ${colors.white};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 32px 10px 32px;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 5%;
  }
`

const Title = styled.h1`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  line-height: 28px;
  width: max-content;
  max-width: 100%;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 16px;
    font-weight: 500;
  }
`

const Subtitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  min-width: max-content;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 14px;
    font-weight: 400;
    min-width: 0;
  }
`

const SmallText = styled.span`
  font-size: 14px;
  font-weight: 400;
  min-width: 300px;
  display: flex;
  align-items: center;
  color: ${colors.grey48};
`

const StyledCol = styled(Col)`
  text-align: center;
  width: max-content;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 8px;
`

const BackButton = styled(Button)`
  color: ${colors.black} !important;
  padding: 0;
  min-width: 0;
  min-height: 0;
  background: transparent;
`

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  border: 1px solid ${colors.grey96};
  padding: 24px 20px;
  flex: 3;
  height: 100%;
  max-width: 700px;
  max-height: 600px;
  overflow: hidden;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    border: none;
    padding: 0px;
    max-width: 100%;
  }
`

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 600px;
  min-height: 600px;
  overflow: hidden;
  max-width: 400px;
  flex: 2;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    max-width: 350px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const StyledRow = styled(Row)`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: row;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: fit-content(100px) 1fr fit-content(100px);
  align-items: start;
  width: 100%;
`

const PreviewModal = ({ listingId }: { listingId: string }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const { data: listing, isLoading: isListingLoading } = useGetSuiteInfo(listingId)

  return (
    <Wrapper>
      <Grid>
        <BackButton onClick={close} theme={'text'}>
          <LeftArrowIcon width={14} height={14} />
          {t('common.back', 'Back')}
        </BackButton>

        <StyledCol>
          <Title>{t('l.boost_listing.preview_modal.title', 'Preview Sponsored Listing')}</Title>
          <Subtitle>
            {t(
              'l.boost_listing.preview_modal.subtitle',
              'This is how your listing will appear to renters using map view.'
            )}
          </Subtitle>
        </StyledCol>
      </Grid>

      <Row style={{ alignItems: 'flex-start' }}>
        <Left>
          <SmallText>
            {t('common.home', 'Home')}
            <Separator width={14} height={14} />
            {`${listing?.state}`}
            <Separator width={14} height={14} />
            {`${listing?.city}`}
          </SmallText>
          <Title style={{ fontSize: isMobile ? '20px' : fontSizes.large }}>
            Canada Apartments, Condos and Houses for Rent
          </Title>
          <Subtitle style={{ marginTop: isMobile ? '-8px' : '0' }}>364 Rentals Found </Subtitle>
          <SmallText style={{ marginTop: '16px' }}>Featured</SmallText>
          <StyledRow style={{ gap: '16px' }}>
            <ListingCard featured={true} listing={listing} loading={isListingLoading} />
            <ListingCard />
            <ListingCard />
          </StyledRow>
          {!isMobile && <SmallText>Most relevant</SmallText>}
          {!isMobile && (
            <Row style={{ gap: '16px' }}>
              <ListingCard blank={true} />
              <ListingCard blank={true} />
            </Row>
          )}
        </Left>
        {!isMobile && (
          <Right>
            <Map />
          </Right>
        )}
      </Row>
    </Wrapper>
  )
}

export const showPreviewModal = createModal(PreviewModal, {
  width: 'auto',
  maxWidth: '90%',
  showBackground: false,
  showClose: true,
  hasHeader: false,
  radius: '16px',
  sheet: true,
  fullSnap: true
})
